import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { useAuth } from '../auth/useAuth';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { IconButton } from '../components/IconButton';
import { IconBack, IconEdit, IconTrash } from '../components/icons';
import { primaryRoleLabel } from '../utils/roles';

// TS port of the source (Falah) app's pages/CustomerDetail.jsx. `users` here
// carries `roles` as plain string[] (CustomerController::show() replaces the
// eager-loaded Role models with name strings before responding).
interface CustomerLinkedUser {
  id: number;
  full_name: string;
  email: string;
  roles: string[];
}

interface CustomerRecord {
  id: number;
  name: string;
  phone: string | null;
  email: string | null;
  notes: string | null;
  quotations_count: number;
  invoices_count: number;
  users?: CustomerLinkedUser[];
}

export function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuth();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [customer, setCustomer] = useState<CustomerRecord | null>(null);

  const canManage = can('revenue.customers.manage');

  useEffect(() => {
    api.get<CustomerRecord>(`/customers/${id}`).then((res) => setCustomer(res.data));
  }, [id]);

  const remove = async () => {
    if (!customer) return;
    if (!(await confirmDelete(`Delete customer "${customer.name}"?`))) return;
    try {
      await api.delete(`/customers/${id}`);
      navigate('/revenue/customers');
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  if (!customer) return null;

  const users = customer.users ?? [];

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/revenue/customers')} />
          {canManage && <IconButton icon={IconEdit} label="Edit" to={`/revenue/customers/${id}/edit`} />}
          {canManage && <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={remove} />}
        </div>
      </header>

      <div className="card">
        <div className="detail-summary">
          <div>
            <div className="detail-summary-title">{customer.name}</div>
          </div>
        </div>

        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-item-label">Phone</span>
            <span className={`detail-item-value${customer.phone ? '' : ' empty'}`}>{customer.phone || 'Not set'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Email</span>
            <span className={`detail-item-value${customer.email ? '' : ' empty'}`}>{customer.email || 'Not set'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Quotations</span>
            <span className="detail-item-value">{customer.quotations_count ?? 0}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Invoices</span>
            <span className="detail-item-value">{customer.invoices_count ?? 0}</span>
          </div>
        </div>

        <div className="detail-item detail-item-full">
          <span className="detail-item-label">Notes</span>
          {customer.notes ? (
            <p className="detail-notes">{customer.notes}</p>
          ) : (
            <span className="detail-item-value empty">Not provided</span>
          )}
        </div>
      </div>

      <div className="card">
        <h2>Linked users</h2>
        <p className="muted">Portal accounts that sign in as this customer.</p>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>{u.full_name}</td>
                  <td className="muted">{u.email}</td>
                  <td className="muted">{primaryRoleLabel(u.roles)}</td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={3} className="muted center">
                    No users linked to this customer.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
